import { EVENT_LOCATION, EVENT_TYPE, useTrackEvent } from '@src/hooks/useTrackEvent';
import React, { useEffect, useState } from 'react';
import { AuctioneerType } from '@src/enums/auctioneer-type-enum';
import { EVENT_TITLE } from '@src/enums/event-title-enum';
import { EquipmentCard } from '@tractorzoom/components';
import Image from 'next/image';
import { LinearProgress } from '@mui/material';
import { SearchResult } from '@tractorzoom/types';
import { useGoTo } from '@src/hooks/use-go-to';
import { useInView } from 'react-intersection-observer';
import useStyles from './styles';

type Props = {
    cards: SearchResult[];
    loadMoreItems?: () => Promise<void>;
    location: EVENT_LOCATION;
    totalResults: number;
};

const CardGrid = ({ cards, location, loadMoreItems, totalResults }: Props) => {
    const classes = useStyles();
    const [lastRowRef, lastRowInView] = useInView();
    const [isLoading, setIsLoading] = useState(false);

    const trackEvent = useTrackEvent();
    const { goToEquipment } = useGoTo();
    const getEventInfo = ({ id, category, featured, make, model, seller, sellerType, subcategory }: SearchResult) => ({
        category,
        featured,
        location,
        make,
        model,
        objectId: id as string,
        seller,
        subcategory,
        type: sellerType === AuctioneerType.DEALER ? EVENT_TYPE.DEALER : EVENT_TYPE.AUCTION,
    });

    const cardImpression = (equipment: SearchResult) => () => {
        trackEvent(EVENT_TITLE.EquipmentCardView, getEventInfo(equipment));
    };

    const handleOpen = (equipment: SearchResult) => () => {
        trackEvent(EVENT_TITLE.EquipmentCardClick, getEventInfo(equipment));
        goToEquipment({
            slug: equipment.id as string,
        });
    };

    useEffect(() => {
        const loadMore = async () => {
            setIsLoading(true);
            if (loadMoreItems) {
                await loadMoreItems();
            }
            setIsLoading(false);
        };

        if (lastRowInView && cards.length < totalResults) {
            void loadMore();
        }
    }, [lastRowInView]);

    return (
        <>
            <div className={classes.grid}>
                {cards.map((item, i) => {
                    const props = { key: `card-grid-${item.id}` };

                    if (i === cards.length - 4) {
                        props['ref'] = lastRowRef;
                    }

                    return (
                        <div data-testid='card' {...props}>
                            <EquipmentCard
                                onVisible={cardImpression(item)}
                                handleOpen={handleOpen(item)}
                                imageComponent={Image}
                                imageUrl={item.images}
                                {...item}
                                price={null}
                            />
                        </div>
                    );
                })}
            </div>
            {isLoading && <LinearProgress data-testid='loading' />}
        </>
    );
};

export default CardGrid;
